import { classNames } from "primereact/utils";
import { DefaultSection } from "../common/DefaultSection";
import styles from "./ItemsList.module.scss";
import { SearchField } from "../inputs/searchField/SearchField";
import {
  IfFalse,
  IfTrue,
  RenderWhen,
} from "../../infrastructure/components/RenderWhen";
import { SwipeableListItem, Type } from "react-swipeable-list";
import { useItemsList } from "./hooks/useItemsList";
import { useItemsListActions } from "./hooks/useItemsListActions";
import { useItemsListVirtualization } from "./hooks/useItemsListVirtualization";
import "react-swipeable-list/dist/styles.css";
import { useAppContext } from "../../../AppContext";
import { IItemsListProps } from "./types/ItemsList.types";
import { useItemsListFilters } from "./hooks/useItemsListFilters";
import { FiltersSidePanel } from "../filters/FiltersSidePanel";
import { FilterIconButton } from "../filters/FilterIconButton";
import { ItemsListFiltersPanelContent } from "./components/ItemsListFiltersPanelContent";

export const ItemsList = <TItem extends { [key: string]: any }>({
  className,
  title,
  actionButton,
  isLoading,
  numberOfItems = 20,
  itemTemplate,
  items,
  noItemsText = "Не бяха открити резултати.",
  enableSearch = false,
  searchClassName,
  searchSuffix,
  headerContent,
  fullHeight = false,
  fullWidth,
  swipeLeftActions,
  swipeRightActions,
  tooltip,
  itemOnClick,
  noWrapper = false,
  footer,
  deletedFilter,
  showItemsCount = false,
  renderAllOnMobile = false,
}: IItemsListProps<TItem>) => {
  // Without the wrapper there is no header to hang the filter trigger on, so
  // the filter is ignored altogether - otherwise it would hide deleted records
  // with no way left to reveal them.
  const effectiveDeletedFilter = noWrapper ? undefined : deletedFilter;

  const {
    activeItemsCount,
    isFiltersPanelOpen,
    openFiltersPanel,
    closeFiltersPanel,
    showDeleted,
    updateShowDeleted,
    clearAllFilters,
    visibleItems,
    activeFiltersCount,
  } = useItemsListFilters(items, effectiveDeletedFilter);

  const { filteredItems, onEdit, onSearch } = useItemsList(
    visibleItems,
    swipeLeftActions,
    swipeRightActions
  );

  const { isDesktop } = useAppContext();

  const { leadingActions, trailingActions, actionButtons } =
    useItemsListActions(onEdit, swipeLeftActions, swipeRightActions);

  // Virtualization and infinite scroll logic
  // Only enable virtualization on desktop when fullHeight is true
  const {
    parentRef,
    virtualizer,
    handleScroll,
    hasMoreMobileItems,
    getMobileVisibleItems,
    isVirtualizationEnabled,
  } = useItemsListVirtualization({
    items: filteredItems,
    isDesktop,
    enableVirtualization: fullHeight,
    batchSize: renderAllOnMobile ? Number.POSITIVE_INFINITY : undefined,
  });

  if (!filteredItems) {
    return <></>;
  }

  const renderLoadingItems = () =>
    Array.from({ length: numberOfItems }).map((_, i) => (
      <div key={i}>
        {itemTemplate(undefined, i, undefined, styles.container_items_item)}
      </div>
    ));

  const renderMobileItem = (item: TItem, index: number) => (
    <SwipeableListItem
      onClick={() => itemOnClick?.(item.id)}
      key={`${item?.id}_${index}`}
      listType={Type.IOS}
      className={styles.actions}
      leadingActions={leadingActions(item)}
      trailingActions={trailingActions(item)}
    >
      {itemTemplate(item, index, undefined, styles.container_items_item)}
    </SwipeableListItem>
  );

  // Virtualized rendering for desktop - only renders visible items
  const renderVirtualizedItems = () => {
    const virtualItems = virtualizer.getVirtualItems();

    return (
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
      >
        {virtualItems.map((virtualRow) => {
          const item = filteredItems[virtualRow.index];
          return (
            <div
              key={`${item?.id}_${virtualRow.index}`}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
              onClick={() => itemOnClick?.(item.id)}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              {itemTemplate(
                item,
                virtualRow.index,
                actionButtons(item),
                styles.container_items_item
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Non-virtualized rendering for mobile (swipeable items) with infinite scroll
  const renderMobileItems = () => {
    const visibleItems = getMobileVisibleItems();
    return (
      <>
        {visibleItems.map((item, index) => renderMobileItem(item, index))}
        {hasMoreMobileItems && (
          <div className={styles.container_items_loadMore}>
            Зареждане на още...
          </div>
        )}
      </>
    );
  };

  // Non-virtualized rendering for desktop - renders all items (used when dynamic height is needed)
  const renderDesktopItems = () => {
    return (
      <>
        {filteredItems.map((item, index) => (
          <div
            key={`${item?.id}_${index}`}
            onClick={() => itemOnClick?.(item.id)}
          >
            {itemTemplate(
              item,
              index,
              actionButtons(item),
              styles.container_items_item
            )}
          </div>
        ))}
      </>
    );
  };

  const containerClasses = classNames(styles.container, className, {
    [styles.container_restrictWidth]: !fullWidth,
    [styles.container_fullHeight]: !!fullHeight,
    [styles.container_autoHeight]: !fullHeight,
  });

  const content = (
    <div
      ref={parentRef}
      onScroll={handleScroll}
      className={classNames(styles.container_items, {
        [styles.container_items_virtualized]: isVirtualizationEnabled,
      })}
    >
      {headerContent}

      <RenderWhen condition={enableSearch}>
        {/* Deliberately two branches rather than one wrapped field: 25 screens share
            this list, and only the ones asking for a suffix may have their search row
            re-parented. Without it the markup is identical to what it always was. */}
        {searchSuffix ? (
          <div className={styles.container_search}>
            <SearchField
              onChange={onSearch}
              className={classNames(searchClassName, styles.container_search_field)}
            />
            {searchSuffix}
          </div>
        ) : (
          <SearchField onChange={onSearch} className={searchClassName} />
        )}
      </RenderWhen>

      <RenderWhen condition={isLoading}>
        <IfTrue>{renderLoadingItems()}</IfTrue>
        <IfFalse>
          <RenderWhen condition={filteredItems?.length === 0 && !isLoading}>
            <div className={styles.container_no_results}>{noItemsText}</div>
          </RenderWhen>

          <RenderWhen
            condition={
              filteredItems?.length > 0 && filteredItems !== undefined
            }
          >
            {isDesktop
              ? isVirtualizationEnabled
                ? renderVirtualizedItems()
                : renderDesktopItems()
              : renderMobileItems()}
          </RenderWhen>
        </IfFalse>
      </RenderWhen>

      {!isLoading && filteredItems?.length > 0 && footer}
    </div>
  );

  if (noWrapper) {
    return <div className={containerClasses}>{content}</div>;
  }

  const headerActionButton = !effectiveDeletedFilter ? (
    actionButton
  ) : (
    <div className={styles.container_headerActions}>
      {actionButton}
      <FilterIconButton
        onClick={openFiltersPanel}
        activeFiltersCount={activeFiltersCount}
      />
    </div>
  );

  return (
    <>
      <DefaultSection
        className={containerClasses}
        title={title}
        titleSuffix={
          showItemsCount && !isLoading ? (
            <span className={styles.container_count}>({activeItemsCount})</span>
          ) : undefined
        }
        tooltip={tooltip}
        actionButton={headerActionButton}
      >
        {content}
      </DefaultSection>

      <RenderWhen condition={!!effectiveDeletedFilter}>
        <FiltersSidePanel
          visible={isFiltersPanelOpen}
          onHide={closeFiltersPanel}
        >
          <ItemsListFiltersPanelContent
            showDeleted={showDeleted}
            sectionTitle={effectiveDeletedFilter?.sectionTitle}
            toggleLabel={effectiveDeletedFilter?.toggleLabel}
            onShowDeletedChange={updateShowDeleted}
            onClearAllFilters={clearAllFilters}
            onClose={closeFiltersPanel}
          />
        </FiltersSidePanel>
      </RenderWhen>
    </>
  );
};
